'use client';

import { useTranslations } from 'next-intl';
import { QrCode, Eye, Check } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useRouter } from '@/i18n/routing';
import type { Registration } from '@/features/registration';

interface StickingCompactRowProps {
  registration: Registration;
  onViewQrData: (registration: Registration) => void;
}

const STAGE_STYLES: Record<string, string> = {
  sticking:
    'bg-amber-100 text-amber-800 hover:bg-amber-100 dark:bg-amber-900 dark:text-amber-200',
  review:
    'bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200',
  completed:
    'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
};

const STAGE_STATUS_STYLES: Record<string, string> = {
  ready_for_numbering:
    'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  ready_for_printing:
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
  returned_for_correction:
    'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200'
};

export function StickingCompactRow({
  registration,
  onViewQrData
}: StickingCompactRowProps) {
  const t = useTranslations('sticking');
  const tc = useTranslations('common');
  const router = useRouter();

  const hasBatch = !!registration.latest_batch_id;

  const stageLabels: Record<string, string> = {
    sticking: tc('stageSticking'),
    review: tc('stageReview'),
    completed: tc('stageCompleted')
  };

  const subStatus = registration.stage_status;
  const subStatusLabels: Record<string, string> = {
    ready_for_numbering: t('subStatusReadyForNumbering'),
    ready_for_printing: t('subStatusReadyForPrinting'),
    returned_for_correction: t('subStatusReturnedForCorrection')
  };

  const totalSheets = hasBatch
    ? (registration.latest_batch_end_page ?? 0) -
      (registration.latest_batch_start_page ?? 0) +
      1
    : null;

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      {/* Left: container number + entity */}
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <button
            type='button'
            className='text-primary truncate font-medium hover:underline'
            onClick={() =>
              router.push(
                `/dashboard/containers/${registration.id}?stage=sticking`
              )
            }
          >
            {registration.container_number}
          </button>
          <span className='text-muted-foreground hidden truncate text-sm sm:inline'>
            {registration.entity_name_ar}
          </span>
        </div>
      </div>

      {/* Center: batch info (hidden on mobile) */}
      {hasBatch && (
        <div className='hidden items-center gap-1.5 md:flex'>
          <span className='text-muted-foreground text-sm'>
            {registration.latest_batch_start_page}–
            {registration.latest_batch_end_page}
          </span>
          <Badge
            variant='secondary'
            className='bg-green-100 px-1.5 py-0 text-[10px] text-green-800 dark:bg-green-900 dark:text-green-200'
          >
            <Check className='me-0.5 h-2.5 w-2.5' />
            {totalSheets} {t('totalSheets').toLowerCase()}
          </Badge>
        </div>
      )}

      {/* Stage badge */}
      <Badge
        variant='secondary'
        className={cn(
          'hidden shrink-0 text-xs sm:inline-flex',
          STAGE_STYLES[registration.current_stage] ?? ''
        )}
      >
        {stageLabels[registration.current_stage] ?? registration.current_stage}
      </Badge>
      {registration.current_stage === 'sticking' &&
        subStatus &&
        subStatusLabels[subStatus] && (
          <Badge
            variant='secondary'
            className={cn(
              'hidden shrink-0 text-xs sm:inline-flex',
              STAGE_STATUS_STYLES[subStatus]
            )}
          >
            {subStatusLabels[subStatus]}
          </Badge>
        )}

      {/* Actions */}
      <div className='flex shrink-0 items-center gap-1'>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7'
          disabled={!hasBatch}
          onClick={() => onViewQrData(registration)}
          title={t('viewQrData')}
        >
          <QrCode className='h-3.5 w-3.5' />
        </Button>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7'
          onClick={() =>
            router.push(
              `/dashboard/containers/${registration.id}?stage=sticking`
            )
          }
          title={t('viewContainer')}
        >
          <Eye className='h-3.5 w-3.5' />
        </Button>
      </div>
    </div>
  );
}
